import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import example from '../data/network-example.json';
import { graphNodeRadius, layoutGraph, nodeKinds, NodeKind, pruneGraph, sanitizeGraph } from '../lib/networkGraph';
import './NetworkDemo.css';

function EntityIcon({ kind }: { kind: NodeKind }) {
  if (kind === 'person') return <><circle cx="0" cy="-5" r="4" /><path d="M-8 9v-2a8 8 0 0 1 16 0v2" /></>;
  if (kind === 'address') return <><path d="M0 11S-8 2-8-3a8 8 0 1 1 16 0c0 5-8 14-8 14Z" /><circle cy="-3" r="3" /></>;
  if (kind === 'vehicle') return <><path d="m-9 1 3-7h12l3 7v7H-9ZM-9 1H9M-6 8v3M6 8v3" /><path d="M-6 4h2m8 0h2" /></>;
  if (kind === 'phone') return <path d="m-7-9 4-1 3 5-3 3a18 18 0 0 0 5 5l3-3 5 3-1 4C1 12-12-1-7-9Z" />;
  if (kind === 'email') return <><rect x="-10" y="-7" width="20" height="14" rx="2" /><path d="m-10-6 10 7 10-7" /></>;
  if (kind === 'account') return <><path d="m-10-5 10-6 10 6ZM-10 10H10M-7-3V7M0-3V7M7-3V7" /></>;
  if (kind === 'case') return <><rect x="-8" y="-10" width="16" height="20" rx="2" /><path d="M-4-5H4M-4 0H4M-4 5H1" /></>;
  if (kind === 'other') return <><circle r="8" /><path d="M-3 0H3M0-3V3" /></>;
  return <><rect x="-8" y="-10" width="16" height="20" rx="2" /><path d="M-4-5h2m4 0h2m-8 5h2m4 0h2M-1 10V5h2v5" /></>;
}

export default function NetworkDemo({ data = example }: { data?: unknown }) {
  const graph = useMemo(() => pruneGraph(sanitizeGraph(data)), [data]);
  const initialNodes = useMemo(() => layoutGraph(graph), [graph]);
  const [nodes, setNodes] = useState(initialNodes);
  useEffect(() => { setNodes(initialNodes); setSelection(null); }, [initialNodes]);
  const [selection, setSelection] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const gesture = useRef<{ pointer: number; key: number | null; x: number; y: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const viewRef = useRef({ zoom, pan });
  viewRef.current = { zoom, pan };
  const [expanded, setExpanded] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const wasExpanded = useRef(false);
  useEffect(() => {
    if (expanded) dialogRef.current?.showModal();
    else if (wasExpanded.current) expandButtonRef.current?.focus();
    wasExpanded.current = expanded;
  }, [expanded]);
  const canvasPoint = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    const matrix = svg?.getScreenCTM();
    if (!svg || !matrix) return null;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    return point.matrixTransform(matrix.inverse());
  }, []);
  const zoomAt = useCallback((next: number, x = 300, y = 230) => {
    const view = viewRef.current;
    const bounded = Math.max(0.4, Math.min(2.4, next));
    const ratio = bounded / view.zoom;
    setPan({ x: x - 300 - (x - 300 - view.pan.x) * ratio, y: y - 230 - (y - 230 - view.pan.y) * ratio });
    setZoom(bounded);
  }, []);
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const wheel = (event: WheelEvent) => {
      const point = canvasPoint(event.clientX, event.clientY);
      if (!point) return;
      event.preventDefault();
      zoomAt(viewRef.current.zoom * Math.exp(-event.deltaY * 0.002), point.x, point.y);
    };
    svg.addEventListener('wheel', wheel, { passive: false });
    return () => svg.removeEventListener('wheel', wheel);
  }, [expanded, canvasPoint, zoomAt]);
  const startDrag = (event: React.PointerEvent, key: number | null) => {
    if (event.button !== 0 || gesture.current) return;
    const point = canvasPoint(event.clientX, event.clientY);
    if (!point) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClick.current = false;
    gesture.current = { pointer: event.pointerId, key, x: point.x, y: point.y, moved: false };
    svgRef.current?.setPointerCapture(event.pointerId);
    setDragging(true);
  };
  const moveDrag = (event: React.PointerEvent) => {
    const active = gesture.current;
    if (!active || active.pointer !== event.pointerId) return;
    const point = canvasPoint(event.clientX, event.clientY);
    if (!point) return;
    const dx = point.x - active.x, dy = point.y - active.y;
    if (!active.moved && Math.hypot(dx, dy) < 3) return;
    active.moved = true;
    if (active.key === null) setPan(value => ({ x: value.x + dx, y: value.y + dy }));
    else setNodes(value => value.map(node => node.key === active.key ? { ...node, x: node.x + dx / zoom, y: node.y + dy / zoom } : node));
    active.x = point.x;
    active.y = point.y;
  };
  const endDrag = (event: React.PointerEvent) => {
    const active = gesture.current;
    if (!active || active.pointer !== event.pointerId) return;
    suppressClick.current = active.moved;
    if (!active.moved && active.key !== null && event.type !== 'pointercancel') setSelection(active.key);
    gesture.current = null;
    setDragging(false);
    if (svgRef.current?.hasPointerCapture(event.pointerId)) svgRef.current.releasePointerCapture(event.pointerId);
  };
  const selected = nodes.find(node => node.key === selection) ?? nodes.find(node => node.focus) ?? nodes[0];
  const connected = new Set<number>();
  graph.edges.forEach(edge => {
    if (edge.from === selected?.key) connected.add(edge.to);
    if (edge.to === selected?.key) connected.add(edge.from);
  });
  const visibleKinds = Array.from(new Set(nodes.map(node => node.kind)));
  const patternId = useId().replace(/:/g, '');
  const nodeRadius = graphNodeRadius(nodes.length);
  const content = (
    <div className="network-demo product-network">
      <div className="demo-toolbar"><span><i className="status-dot" /> Network explorer</span><span className="demo-badge">CONNECTED ENTITIES</span></div>
      <div className="product-graph-canvas">
        <div className="network-context"><span className="focus-indicator" /> Company in focus</div>
        <svg ref={svgRef} className={`interactive-network ${dragging ? 'is-dragging' : ''}`} viewBox="0 0 600 460" role="group" aria-label="Interactive network of companies, people, addresses and other entities"
          onPointerDown={event => startDrag(event, null)} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag} onLostPointerCapture={endDrag}>
          <defs><pattern id={patternId} width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="0.8" fill="#dce2dc" /></pattern></defs>
          <rect width="600" height="460" fill={`url(#${patternId})`} />
          <g transform={`translate(${300 + pan.x} ${230 + pan.y}) scale(${zoom}) translate(-300 -230)`}>
            {graph.edges.map(edge => {
              const from = nodes[edge.from], to = nodes[edge.to];
              const highlighted = edge.from === selected?.key || edge.to === selected?.key;
              return <line key={`${edge.from}-${edge.to}-${graph.edges.indexOf(edge)}`} x1={from.x} y1={from.y} x2={to.x} y2={to.y} className={`network-edge ${highlighted ? 'is-connected' : ''}`} />;
            })}
            {nodes.map(node => (
              <g key={node.key} transform={`translate(${node.x} ${node.y})`} className={`entity-node entity-${node.kind} ${node.focus ? 'is-focus' : ''} ${node.key === selected?.key ? 'is-selected' : ''} ${connected.has(node.key) ? 'is-connected' : ''}`}
                role="button" tabIndex={0} aria-label={`Explore ${nodeKinds[node.kind].label} node ${node.key + 1}`} aria-pressed={node.key === selected?.key}
                onPointerDown={event => startDrag(event, node.key)} onClick={() => { if (!suppressClick.current) setSelection(node.key); suppressClick.current = false; }} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelection(node.key); } }}>
                <title>{nodeKinds[node.kind].label}</title>
                <circle r={node.focus ? nodeRadius + 15 : nodeRadius + 5} className="entity-halo" />
                <circle r={node.focus ? nodeRadius + 8 : nodeRadius} className="entity-circle" />
                <g className="entity-icon" transform={`scale(${node.focus ? (nodeRadius + 8) / 22 : nodeRadius / 22})`}><EntityIcon kind={node.kind} /></g>
              </g>
            ))}
          </g>
        </svg>
        {!nodes.length && <p className="network-empty">No network to display yet.</p>}
        <div className="network-controls" aria-label="Graph view controls">
          <button aria-label="Zoom in" disabled={zoom >= 2.4} onClick={() => zoomAt(Number((zoom + 0.2).toFixed(1)))}>+</button>
          <button aria-label="Zoom out" disabled={zoom <= 0.4} onClick={() => zoomAt(Number((zoom - 0.2).toFixed(1)))}>−</button>
          <button aria-label="Reset graph view" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); setNodes(initialNodes); setSelection(null); }}>↺</button>
          <button ref={expandButtonRef} aria-label={expanded ? 'Close expanded graph' : 'Expand graph'} onClick={() => setExpanded(value => !value)}>{expanded ? '×' : '⤢'}</button>
        </div>
      </div>
      <div className="entity-legend" aria-label="Node types">{visibleKinds.map(kind => <span key={kind}><svg viewBox="-13 -13 26 26" className={`legend-icon entity-${kind}`} aria-hidden="true"><circle r="13" /><g className="entity-icon" transform="scale(.7)"><EntityIcon kind={kind} /></g></svg>{nodeKinds[kind].label}</span>)}</div>
      <div className="network-selection" aria-live="polite">
        <div><span className="detail-label">{selected?.focus ? 'IN FOCUS' : 'SELECTED NODE TYPE'}</span><strong>{selected ? nodeKinds[selected.kind].label : 'Network'}</strong></div>
        <p>{selected ? nodeKinds[selected.kind].description : 'Add a graph to explore its connections.'}</p>
        <span className="connection-count"><b>{connected.size}</b><small>connections</small></span>
      </div>
      <div className="demo-footnote"><span>Drag nodes · Drag background to pan</span><span>Scroll to zoom · Click to explore</span></div>
    </div>
  );
  return expanded ? <dialog ref={dialogRef} className="network-dialog" aria-label="Network explorer" onCancel={() => setExpanded(false)} onClose={() => setExpanded(false)}>{content}</dialog> : content;
}
